import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSSEStore, type SkillCardData } from '../../store/useSSEStore';
import { useProjectStore } from '../../store/useProjectStore';
import { SKILL_NAMES, SKILL_LABELS, type AgentEvent } from '../../types';
import * as api from '../../api/endpoints';
import { projectPath } from '../../api/projectContext';
import { ProjectChat } from './ProjectChat';

export function AgentTab() {
  return (
    <div className="agent-container" style={{ flexDirection: 'column' }}>
      <HorizontalStepper />
      <SummaryCards />
      <div className="agent-main">
        <ApprovalCard />
        <AgentFeed />
        <ProjectChat />
      </div>
    </div>
  );
}

function HorizontalStepper() {
  const skillStatuses = useSSEStore((s) => s.skillStatuses);
  const skillProgress = useSSEStore((s) => s.skillProgress);

  return (
    <div className="pipeline-stepper">
      {SKILL_NAMES.map((name, i) => {
        const skill = skillStatuses[name];
        const status = skill?.status || 'pending';
        const label = SKILL_LABELS[name] || name;
        const progress = skillProgress[name];

        return (
          <span key={name} style={{ display: 'contents' }}>
            {i > 0 && (
              <span className={`stepper-connector ${status === 'success' || status === 'skipped' ? 'done' : ''}`} />
            )}
            <span className={`stepper-step step-${status}`}>
              <span className={`step-dot ${status}`} />
              <span className="step-label">{label}</span>
              {status === 'running' && progress && progress.total > 0 && (
                <span className="step-progress">{progress.processed}/{progress.total}</span>
              )}
              {status === 'running' && skill?.startTime && (
                <RunningTimer startTime={skill.startTime} />
              )}
              {skill?.elapsed && status !== 'running' && (
                <span className="step-elapsed">{(skill.elapsed / 1000).toFixed(1)}s</span>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
}

function SummaryCards() {
  const project = useProjectStore((s) => s.project);
  const triage = useProjectStore((s) => s.triage);
  const separation = useProjectStore((s) => s.separation);
  const extraction = useProjectStore((s) => s.extraction);
  const boqExtraction = useProjectStore((s) => s.boqExtraction);

  const fileCount = project?.files?.length || 0;
  const pageCount = triage?.classifications?.length || 0;

  // Page type breakdown from triage
  const pageTypes = triage?.summary as Record<string, number> | undefined;

  // Zones from separation
  const zoneCount = separation?.zones?.length || 0;

  // Components from extraction
  const componentCount = extraction?.zones?.reduce(
    (sum, z) => sum + (z.all_components?.length || 0), 0,
  ) || 0;

  // BoQ positions from boq_extraction
  const boqPositions = (boqExtraction as unknown as Record<string, unknown>)?.total_positions as number || 0;

  if (fileCount === 0 && pageCount === 0) return null;

  // Build cards dynamically — only show what's available
  const cards: Array<{ value: string | number; label: string; color?: string }> = [];

  cards.push({ value: fileCount, label: 'Files' });

  if (pageCount > 0) {
    cards.push({ value: pageCount, label: 'Pages' });
  }

  // Show key page type counts from triage
  if (pageTypes) {
    const sldCount = (pageTypes.single_line_diagram || 0) + (pageTypes.panel_schedule || 0);
    const fpCount = (pageTypes.floor_plan_power || 0) + (pageTypes.floor_plan_lighting || 0) + (pageTypes.floor_plan_data || 0);
    const boqCount = pageTypes.boq || 0;

    if (sldCount > 0) cards.push({ value: sldCount, label: 'SLD/Panel' });
    if (fpCount > 0) cards.push({ value: fpCount, label: 'Floor Plans' });
    if (boqCount > 0) cards.push({ value: boqCount, label: 'BoQ Pages' });
  }

  if (zoneCount > 0) {
    cards.push({ value: zoneCount, label: 'Zones' });
  }

  if (componentCount > 0) {
    cards.push({ value: componentCount, label: 'Components' });
  }

  if (boqPositions > 0) {
    cards.push({ value: boqPositions, label: 'BoQ Positions' });
  }

  return (
    <div className="summary-cards">
      {cards.map((card) => (
        <div className="summary-card" key={card.label}>
          <span className="summary-card-value" style={card.color ? { color: card.color } : undefined}>
            {card.value}
          </span>
          <span className="summary-card-label">{card.label}</span>
        </div>
      ))}
    </div>
  );
}

function AgentFeed() {
  const events = useSSEStore((s) => s.agentEvents);
  const feedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [events.length]);

  if (events.length === 0) {
    return (
      <div className="agent-feed" ref={feedRef}>
        <div className="feed-placeholder">Waiting for agent events...</div>
      </div>
    );
  }

  return (
    <div className="agent-feed" ref={feedRef}>
      {events.map((evt, i) => (
        <AgentEventCard key={i} event={evt} />
      ))}
    </div>
  );
}

function ApprovalCard() {
  const pendingApproval = useSSEStore((s) => s.pendingApproval);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [instructions, setInstructions] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!pendingApproval) return null;

  const skillName = pendingApproval.skill_name || pendingApproval.skill || '';
  const skillLabel = SKILL_LABELS[skillName] || skillName || 'Unknown';
  const isResultReview = Boolean(pendingApproval.result_summary);
  const isFloorPlan = skillName === 'floor_plan_annotation';
  const goAnnotation = () => {
    if (projectId) navigate(projectPath(projectId, 'annotation'));
  };

  const handleApprove = async () => {
    setBusy(true);
    setError('');
    const reqId = pendingApproval.request_id;

    let result;
    if (isResultReview) {
      result = await api.approveResult(reqId);
    } else {
      result = await api.approve(reqId, instructions || undefined);
    }

    if (result) {
      useSSEStore.getState().setPendingApproval(null);
      setInstructions('');
      // Navigate to annotation tab after approving floor plan skill
      if (isFloorPlan) {
        goAnnotation();
      }
    } else {
      const alt = isResultReview
        ? await api.approve(reqId, instructions || undefined)
        : await api.approveResult(reqId);
      if (alt) {
        useSSEStore.getState().setPendingApproval(null);
        setInstructions('');
        if (isFloorPlan) {
          goAnnotation();
        }
      } else {
        setError('Approval failed — request may have timed out.');
      }
    }
    setBusy(false);
  };

  const handleReject = async () => {
    setBusy(true);
    setError('');
    const reqId = pendingApproval.request_id;

    let result;
    if (isResultReview) {
      result = await api.rerun(reqId, instructions || undefined);
    } else {
      result = await api.reject(reqId, instructions || undefined);
    }

    if (result) {
      useSSEStore.getState().setPendingApproval(null);
      setInstructions('');
    } else {
      setError('Reject failed — request may have timed out.');
    }
    setBusy(false);
  };

  const handleDismiss = () => {
    useSSEStore.getState().setPendingApproval(null);
    setInstructions('');
    setError('');
  };

  return (
    <div className="approval-card">
      <div className="approval-card-header">
        <span className="research-gate-badge">
          {isResultReview ? 'REVIEW RESULT' : isFloorPlan ? 'START SKILL' : 'APPROVE SKILL'}
        </span>
        <span className="approval-skill">{skillLabel}</span>
        <button
          className="btn-sm"
          onClick={handleDismiss}
          title="Dismiss"
          style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 14 }}
        >
          &times;
        </button>
      </div>
      {pendingApproval.agent_reasoning && (
        <div className="approval-context" style={{ marginBottom: 8 }}>
          {pendingApproval.agent_reasoning}
        </div>
      )}
      {isResultReview && pendingApproval.result_summary && (
        <div className="approval-context" style={{ marginBottom: 8 }}>
          {pendingApproval.result_summary}
        </div>
      )}
      {pendingApproval.instructions && (
        <div className="approval-instructions-section">
          <label className="approval-label">Agent instructions</label>
          <pre className="event-body" style={{ fontSize: 12, maxHeight: 80, overflow: 'auto' }}>
            {pendingApproval.instructions}
          </pre>
        </div>
      )}
      <div className="approval-instructions-section">
        <textarea
          className="approval-instructions"
          rows={2}
          placeholder={isResultReview ? 'Notes for re-run...' : 'Edit instructions or leave empty...'}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </div>
      {error && (
        <div style={{ color: 'var(--red)', fontSize: 12, padding: '4px 8px' }}>
          {error}
        </div>
      )}
      {isFloorPlan && (
        <div className="approval-context" style={{ marginBottom: 8, fontSize: 11, color: 'var(--text-dim)' }}>
          Approving will start floor plan symbol counting. You'll annotate each page in the Annotation tab.
        </div>
      )}
      <div className="approval-actions">
        <button className="btn-approve" onClick={handleApprove} disabled={busy}>
          {busy ? 'Sending...' : isResultReview ? 'Accept Result' : isFloorPlan ? 'Start Counting' : 'Approve'}
        </button>
        <button className="btn-reject" onClick={handleReject} disabled={busy}>
          {isResultReview ? 'Re-run' : isFloorPlan ? 'Skip Floor Plans' : 'Skip'}
        </button>
      </div>
    </div>
  );
}

function AgentEventCard({ event }: { event: AgentEvent }) {
  const type = event.type;

  if (!type) return null;
  if (type === 'approval_request') return null;

  if (type === 'tool_call') {
    const toolName = event.tool_name || '';
    const skillName = toolName.replace(/^run_/, '');
    const label = SKILL_LABELS[skillName] || toolName;
    const isSkillRun = toolName.startsWith('run_');

    if (isSkillRun) {
      return <SkillCard name={skillName} label={label} />;
    }

    return (
      <div className="event-card">
        <div className="event-header">
          <span className="event-type tool_call">TOOL</span>
          <span className="event-name">{label}</span>
          {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
        </div>
        {event.args && (
          <div className="event-body">
            {Object.entries(event.args)
              .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
              .join('\n')}
          </div>
        )}
      </div>
    );
  }

  if (type === 'tool_result') {
    const toolName = event.tool_name || '';
    if (toolName.startsWith('run_')) return null;

    return (
      <div className="event-card">
        <div className="event-header">
          <span className="event-type tool_result">RESULT</span>
          <span className="event-name">{toolName}</span>
          {event.elapsed_ms && (
            <span className="event-time">{(event.elapsed_ms / 1000).toFixed(1)}s</span>
          )}
        </div>
        {event.summary && <div className="event-body">{event.summary}</div>}
      </div>
    );
  }

  if (type === 'thinking') {
    const text = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
    return (
      <details className="reasoning-block">
        <summary className="reasoning-toggle">Agent reasoning</summary>
        <div className="event-body">{text}</div>
      </details>
    );
  }

  if (type === 'plan_set') {
    return <PlanSetCard event={event} />;
  }

  if (type === 'turn_start') {
    return (
      <div className="event-card">
        <div className="event-header">
          <span className="event-type turn_start">TURN {event.turn}</span>
          {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
        </div>
      </div>
    );
  }

  if (type === 'finalize') {
    return <FinalResultsCard event={event} />;
  }

  if (type === 'fallback') {
    return (
      <div className="event-card">
        <div className="event-header">
          <span className="event-type fallback">FALLBACK</span>
        </div>
        <div className="event-body">
          Agent loop failed — running fallback pipeline
        </div>
      </div>
    );
  }

  return (
    <div className="event-card">
      <div className="event-header">
        <span className={`event-type ${type || ''}`}>{(type || 'event').toUpperCase()}</span>
        {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
      </div>
      {event.data != null && (
        <div className="event-body">
          {typeof event.data === 'string' ? event.data : JSON.stringify(event.data, null, 2)}
        </div>
      )}
    </div>
  );
}

function PlanSetCard({ event }: { event: AgentEvent }) {
  // Try to parse plan steps from the data
  const data = event.data;
  let steps: string[] = [];

  if (typeof data === 'string') {
    // Try parsing as JSON array of strings
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        steps = parsed.map(String);
      }
    } catch {
      // Split by newlines if it has numbered items
      steps = data.split('\n').filter(Boolean);
    }
  } else if (event.plan) {
    steps = event.plan.split('\n').filter(Boolean);
  }

  if (steps.length === 0) {
    return (
      <div className="event-card">
        <div className="event-header">
          <span className="event-type tool_call">PLAN</span>
          {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
        </div>
        <div className="event-body">{typeof data === 'string' ? data : JSON.stringify(data)}</div>
      </div>
    );
  }

  return (
    <div className="event-card">
      <div className="event-header">
        <span className="event-type tool_call">PLAN</span>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{steps.length} steps</span>
        {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
      </div>
      <ol className="plan-checklist">
        {steps.map((step, i) => (
          <li key={i}>
            <span className="plan-step-number">{i + 1}.</span>
            <span>{step.replace(/^\d+[\.\)]\s*/, '')}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function SkillCard({ name, label }: { name: string; label: string }) {
  const skill = useSSEStore((s) => s.skillStatuses[name]);
  const progress = useSSEStore((s) => s.skillProgress[name]);
  const status = skill?.status || 'running';

  const statusClass = status === 'success' ? 'skill-card-success'
    : status === 'failed' ? 'skill-card-failed'
    : 'skill-card-running';

  const icon = status === 'success' ? '\u2713'
    : status === 'failed' ? '\u2717'
    : '\u25CB';

  const elapsed = skill?.elapsed
    ? `${(skill.elapsed / 1000).toFixed(1)}s`
    : status === 'running' ? <RunningTimer startTime={skill?.startTime} /> : '';

  return (
    <div className={`skill-card ${statusClass}`}>
      <div className="skill-card-header">
        <span className="skill-card-icon">{icon}</span>
        <span className="skill-card-name">{label}</span>
        {status === 'running' && progress && progress.total > 0 && (
          <span className="skill-card-progress">
            {progress.processed}/{progress.total}
            {progress.detail && <span className="skill-card-detail"> {progress.detail}</span>}
          </span>
        )}
        <span className={`skill-card-status ${status}`}>{status}</span>
        <span className="skill-card-elapsed">{elapsed}</span>
      </div>
      {skill?.summary && (
        <div className="skill-card-body">{skill.summary}</div>
      )}
    </div>
  );
}

function RunningTimer({ startTime }: { startTime?: number }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!startTime) return;
    const iv = setInterval(() => {
      if (ref.current) {
        const s = ((Date.now() - startTime) / 1000).toFixed(0);
        ref.current.textContent = `${s}s`;
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [startTime]);

  return <span ref={ref} className="step-elapsed">0s</span>;
}

function FinalResultsCard({ event }: { event: AgentEvent }) {
  const metrics = event.metrics as Record<string, unknown> | undefined;
  return (
    <div className="event-card skill-card-success">
      <div className="event-header">
        <span className="event-type finalize">COMPLETE</span>
        {event.timestamp && <span className="event-time">{formatTime(event.timestamp)}</span>}
      </div>
      <div className="event-body">
        {event.plan && <div style={{ marginBottom: 4 }}>{event.plan}</div>}
        {event.final_observations && event.final_observations.length > 0 && (
          <div>
            {event.final_observations.map((obs, i) => (
              <div key={i}>{obs}</div>
            ))}
          </div>
        )}
        {metrics && (
          <div style={{ marginTop: 8 }}>
            {Object.entries(metrics).map(([k, v]) => (
              <div key={k}>
                <strong>{k}:</strong> {String(v)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString();
  } catch {
    return ts;
  }
}
